import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CaretLeft, CaretRight, CurrencyDollar, Coins, Package, Warning,
  Receipt, TrendUp, CreditCard,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BackButton from "@/components/app/BackButton";

// ─── helpers ─────────────────────────────────────────────────────────────

const fmtMoney = (cents, currency = "usd") => {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  });
};

const monthLabel = (ym) => {
  // ym = "YYYY-MM"
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

const todayMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

// Human labels for our payment_method enum
const METHOD_LABELS = {
  stripe: "Stripe",
  cash: "Cash",
  check: "Check",
  yappy: "Yappy",
  ach: "ACH",
  zelle: "Zelle",
  bank: "Bank transfer",
  credit: "Credit card",
  other: "Other",
  unknown: "Unspecified",
};

// ─── page ────────────────────────────────────────────────────────────────

export default function Revenue() {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/revenue-summary", { params: { month: m } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(month); }, [month]);

  const isCurrentMonth = month === todayMonth();

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto space-y-8" data-testid="revenue-dashboard">
      <BackButton />

      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="hs-label">Coach · Head coach</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2" data-testid="revenue-title">
            Revenue
          </h1>
          <p className="text-ink-2 text-sm mt-1">
            Package sales, credits owed, and upcoming pack expirations.
          </p>
        </div>

        <div className="inline-flex items-center gap-1 rounded-2xl border border-line bg-surface p-1"
             data-testid="month-picker">
          <Button
            size="sm"
            variant="ghost"
            className="rounded-xl h-9 px-3"
            onClick={() => setMonth(shiftMonth(month, -1))}
            data-testid="prev-month-button"
            aria-label="Previous month"
          >
            <CaretLeft size={16} weight="bold" />
          </Button>
          <div
            className="px-4 min-w-[160px] text-center font-heading text-sm font-semibold text-ink"
            data-testid="current-month-label"
          >
            {monthLabel(month)}
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-xl h-9 px-3"
            onClick={() => setMonth(shiftMonth(month, +1))}
            data-testid="next-month-button"
            aria-label="Next month"
            disabled={isCurrentMonth}
          >
            <CaretRight size={16} weight="bold" />
          </Button>
          {!isCurrentMonth && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl h-9 px-3 text-terracotta"
              onClick={() => setMonth(todayMonth())}
              data-testid="jump-to-today-button"
            >
              Today
            </Button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="hs-card p-10 text-center text-ink-3" data-testid="revenue-loading">
          Loading revenue…
        </div>
      ) : (
        <>
          <StatCards data={data} />
          <div className="grid lg:grid-cols-2 gap-6">
            <RevenueByPack rows={data.revenue_by_pack} />
            <RevenueByMethod byMethod={data.revenue_by_method} />
          </div>
          <ExpiringSoon rows={data.expiring_soon} />
          <RecentGrants rows={data.recent_grants} />
        </>
      )}
    </div>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────

function StatCards({ data }) {
  const cards = [
    {
      key: "revenue",
      label: "Collected this month",
      value: fmtMoney(data.revenue_cents),
      sub: `${data.packs_sold_count} pack${data.packs_sold_count === 1 ? "" : "s"} sold`,
      Icon: CurrencyDollar,
      tone: "moss",
    },
    {
      key: "active",
      label: "Active packs",
      value: String(data.active_packs_count),
      sub: "with credits remaining",
      Icon: Package,
      tone: "sage",
    },
    {
      key: "owed",
      label: "Outstanding credit",
      value: fmtMoney(data.outstanding_credit_cents),
      sub: `${data.outstanding_credit_packs} pack${data.outstanding_credit_packs === 1 ? "" : "s"} unpaid`,
      Icon: Coins,
      tone: "warning",
    },
    {
      key: "expiring",
      label: "Expiring in 30 days",
      value: String(data.expiring_soon.length),
      sub: "packs with credits left",
      Icon: Warning,
      tone: "terracotta",
    },
  ];
  const toneMap = {
    moss: "bg-moss text-white",
    sage: "bg-sage text-moss",
    warning: "bg-warning/15 text-warning",
    terracotta: "bg-terracotta/15 text-terracotta",
  };
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-stat-cards">
      {cards.map(({ key, label, value, sub, Icon, tone }) => (
        <div key={key} className="hs-card p-5" data-testid={`stat-${key}`}>
          <div className={`h-9 w-9 rounded-xl grid place-items-center ${toneMap[tone]}`}>
            <Icon size={18} weight="bold" />
          </div>
          <div className="mt-3 hs-label text-[10px]">{label}</div>
          <div className="mt-1 font-heading text-2xl font-semibold text-ink" data-testid={`stat-${key}-value`}>
            {value}
          </div>
          <div className="text-xs text-ink-3 mt-0.5">{sub}</div>
        </div>
      ))}
    </div>
  );
}

function RevenueByPack({ rows }) {
  const total = rows.reduce((s, r) => s + (r.cents || 0), 0);
  return (
    <section className="hs-card p-5" data-testid="revenue-by-pack">
      <div className="flex items-center gap-2 mb-4">
        <TrendUp size={18} weight="bold" className="text-moss" />
        <div className="hs-label">Revenue by pack</div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-3">No packs sold this month.</p>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => {
            const pct = total > 0 ? Math.round((r.cents / total) * 100) : 0;
            return (
              <li key={r.package_name} data-testid={`pack-row-${r.package_name}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-ink truncate">{r.package_name}</span>
                  <span className="font-data text-sm text-ink-2">
                    {fmtMoney(r.cents)} <span className="text-ink-3 text-xs">· {r.count}x</span>
                  </span>
                </div>
                <div className="h-1.5 mt-1 rounded-full bg-muted2 overflow-hidden">
                  <div className="h-full bg-moss" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RevenueByMethod({ byMethod }) {
  const entries = Object.entries(byMethod)
    .map(([k, v]) => ({ key: k, label: METHOD_LABELS[k] || k, ...v }))
    .sort((a, b) => b.cents - a.cents);
  const total = entries.reduce((s, r) => s + (r.cents || 0), 0);
  return (
    <section className="hs-card p-5" data-testid="revenue-by-method">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={18} weight="bold" className="text-moss" />
        <div className="hs-label">Revenue by method</div>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-ink-3">No payments collected this month.</p>
      ) : (
        <ul className="space-y-2.5">
          {entries.map((r) => {
            const pct = total > 0 ? Math.round((r.cents / total) * 100) : 0;
            return (
              <li key={r.key} data-testid={`method-row-${r.key}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-ink">{r.label}</span>
                  <span className="font-data text-sm text-ink-2">
                    {fmtMoney(r.cents)} <span className="text-ink-3 text-xs">· {r.count}x</span>
                  </span>
                </div>
                <div className="h-1.5 mt-1 rounded-full bg-muted2 overflow-hidden">
                  <div className="h-full bg-terracotta" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ExpiringSoon({ rows }) {
  return (
    <section className="hs-card p-5" data-testid="expiring-soon">
      <div className="flex items-center gap-2 mb-4">
        <Warning size={18} weight="bold" className="text-terracotta" />
        <div className="hs-label">Expiring in the next 30 days</div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-3">Nothing expiring soon.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line">
          <table className="w-full font-data text-sm">
            <thead className="bg-muted2 text-ink-2">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Athlete</th>
                <th className="text-left px-4 py-2.5 font-medium">Pack</th>
                <th className="text-left px-4 py-2.5 font-medium">Credits left</th>
                <th className="text-left px-4 py-2.5 font-medium">Expires</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pack_id} className="border-t border-line" data-testid={`expiring-row-${r.pack_id}`}>
                  <td className="px-4 py-2.5 font-semibold text-ink">{r.athlete_name}</td>
                  <td className="px-4 py-2.5 text-ink-2">{r.package_name}</td>
                  <td className="px-4 py-2.5 text-ink-2">{r.sessions_remaining}</td>
                  <td className="px-4 py-2.5 text-ink-2">{formatDate(r.expires_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Link
                      to={`/athletes/${r.athlete_id}/billing`}
                      className="text-terracotta text-xs font-semibold hover:underline"
                      data-testid={`expiring-view-${r.pack_id}`}
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RecentGrants({ rows }) {
  return (
    <section className="hs-card p-5" data-testid="recent-grants">
      <div className="flex items-center gap-2 mb-4">
        <Receipt size={18} weight="bold" className="text-moss" />
        <div className="hs-label">Recent grants</div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-3">No grants yet.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line">
          <table className="w-full font-data text-sm">
            <thead className="bg-muted2 text-ink-2">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 font-medium">Athlete</th>
                <th className="text-left px-4 py-2.5 font-medium">Pack</th>
                <th className="text-left px-4 py-2.5 font-medium">Method</th>
                <th className="text-right px-4 py-2.5 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pack_id} className="border-t border-line" data-testid={`grant-row-${r.pack_id}`}>
                  <td className="px-4 py-2.5 text-ink-2">
                    {formatDate(r.paid_at || r.purchased_at)}
                  </td>
                  <td className="px-4 py-2.5 font-semibold text-ink">{r.athlete_name}</td>
                  <td className="px-4 py-2.5 text-ink-2">{r.package_name}</td>
                  <td className="px-4 py-2.5 text-ink-2">
                    {r.payment_status === "credit" ? (
                      <span className="text-warning font-semibold">Credit · owed</span>
                    ) : (
                      METHOD_LABELS[r.payment_method] || r.payment_method || METHOD_LABELS[r.source] || "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-data text-ink">
                    {fmtMoney(r.amount_paid_cents, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
